import { inViewMargin } from '@helpers/animate';
import { formatDate, formatDateISO } from '@helpers/formatters';
import { imageUrl, srcSet } from '@helpers/image';
import { Device, from } from '@helpers/media';
import { useInView } from '@hooks/useInView';
import IndicatorArrow from '@img/icons/indicator-arrow.svg';
import SvgArrow from '@img/icons/share-price-arrow.svg';
import InPageNavigation from '@stories/Components/InPageNavigation/InPageNavigation';
import LinkButton from '@stories/Components/LinkButton/LinkButton';
import * as React from 'react';
import S from './ContentHeroBanner.styles';
import Breadcrumbs from '@stories/Components/Breadcrumbs/Breadcrumbs';
const ContentHeroBanner = ({ bgColor = 'grey', breadcrumbs, content, cta, downArrowSupportText, freeTextOne, freeTextTwo, id, image, inPageNavigation, logo, shareData, showDownArrow = false, subtitle, tint = 0.3, title, variant = 'default', video, }) => {
    const [refContainer, inView] = useInView({ rootMargin: inViewMargin });
    const scrollToArticle = () => {
        if (refContainer.current) {
            window.scrollTo({
                top: refContainer.current.getBoundingClientRect().bottom,
                behavior: 'smooth',
            });
        }
    };
    return (React.createElement(S.Container, { ref: refContainer, id: id, style: { '--tintAmount': tint }, "data-header-overlap": "true", "data-inview": inView, "data-variant": variant, "data-bgColor": bgColor },
        variant !== 'contained' &&
            React.createElement(S.MediaWrapper, null,
                image && (React.createElement("picture", null,
                    React.createElement("source", { media: from(Device.Desktop), srcSet: srcSet(image, {
                            width: 1920,
                            height: variant === 'short' ? 600 : 1080,
                            rmode: 'crop',
                            quality: 90,
                        }) }),
                    React.createElement("source", { media: from(Device.DesktopSmall), srcSet: srcSet(image, {
                            width: 1365,
                            height: variant === 'short' ? 600 : 767,
                            rmode: 'crop',
                            quality: 90,
                        }) }),
                    React.createElement("source", { media: from(Device.Tablet), srcSet: srcSet(image, {
                            width: 1279,
                            height: variant === 'short' ? 300 : 1023,
                            rmode: 'crop',
                            quality: 90,
                        }) }),
                    React.createElement("source", { media: from(Device.MobileLarge), srcSet: srcSet(image, {
                            width: 767,
                            height: variant === 'short' ? 300 : 1023,
                            rmode: 'crop',
                            quality: 90,
                        }) }),
                    React.createElement("source", { media: from(Device.Mobile), srcSet: srcSet(image, {
                            width: 479,
                            height: variant === 'short' ? 300 : 900,
                            rmode: 'crop',
                            quality: 90,
                        }) }),
                    React.createElement(S.Image, { alt: image.altText, loading: "eager", src: imageUrl(image, {
                            width: 375,
                            height: variant === 'short' ? 300 : 775,
                            rmode: 'crop',
                            quality: 90,
                        }), srcSet: srcSet(image, {
                            width: 375,
                            height: variant === 'short' ? 300 : 775,
                            rmode: 'crop',
                            quality: 90,
                        }) }))),
                video && (React.createElement(S.Video, { autoPlay: true, muted: true, playsInline: true, loop: true },
                    React.createElement("source", { src: video, type: "video/mp4" })))),
        React.createElement(S.Inner, null,
            breadcrumbs && breadcrumbs?.length > 0 &&
                React.createElement(Breadcrumbs, { items: breadcrumbs }),
            variant === 'contained' &&
                React.createElement(S.MediaWrapper, null, image && (React.createElement("picture", null,
                    React.createElement("source", { media: from(Device.Desktop), srcSet: srcSet(image, {
                            width: 1604,
                            height: 1364,
                            quality: 90,
                        }) }),
                    React.createElement("source", { media: from(Device.DesktopSmall), srcSet: srcSet(image, {
                            width: 1232,
                            height: 1104,
                            quality: 90,
                        }) }),
                    React.createElement(S.Image, { alt: image.altText, loading: "eager", src: imageUrl(image, {
                            width: 976,
                            height: 875,
                            quality: 90,
                        }), srcSet: srcSet(image, {
                            width: 976,
                            height: 875,
                            quality: 90,
                        }) })))),
            React.createElement(S.ContentWrapper, null,
                React.createElement(S.Title, { id: title.replaceAll(/[\W_]+/g, '-').toLowerCase() }, title),
                content && React.createElement(S.Content, null, content),
                cta && (React.createElement(S.CtaWrapper, null,
                    React.createElement(LinkButton, { buttonType: "inverted", href: cta.url, target: cta.target }, cta.text)))),
            React.createElement(S.ArticleContentWrapper, null,
                subtitle && React.createElement(S.Subtitle, { dangerouslySetInnerHTML: { __html: subtitle } }),
                logo && React.createElement(S.Logo, { alt: logo.altText ?? `logo`, src: imageUrl(logo, { width: 360 }) })),
            (freeTextOne || freeTextTwo) && (React.createElement(S.ArticleAdditionalContent, null,
                freeTextOne && React.createElement(S.FreeText, null, freeTextOne),
                freeTextTwo && React.createElement(S.FreeText, null, freeTextTwo))),
            shareData && (React.createElement(S.ShareWrapper, null,
                React.createElement(S.ShareTable, null,
                    React.createElement(S.ShareLabel, null, "Symbol"),
                    React.createElement(S.ShareValue, null, shareData.symbol),
                    React.createElement(S.ShareLabel, null, "Last"),
                    React.createElement(S.ShareValue, null, shareData.last),
                    React.createElement(S.ShareLabel, null, "Change (%)"),
                    React.createElement(S.ShareValue, null,
                        shareData.change,
                        shareData.changeSign === 'positive' ? (React.createElement(SvgArrow, { "aria-hidden": true, style: { transform: 'rotate(180deg)' } })) : (React.createElement(SvgArrow, { "aria-hidden": true }))),
                    React.createElement(S.ShareLabel, null, "Current"),
                    React.createElement(S.ShareValue, null, shareData.bid)),
                React.createElement(S.ShareTimestamp, { dateTime: formatDateISO(shareData.timestamp) }, formatDate(shareData.timestamp, {
                    second: '2-digit',
                    minute: '2-digit',
                    hour: '2-digit',
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                })))),
            variant === 'article' && showDownArrow && (React.createElement(S.ReadArticle, { onClick: scrollToArticle },
                React.createElement("p", null, downArrowSupportText ?? 'Read article'),
                React.createElement(IndicatorArrow, null))),
            variant === 'article' && inPageNavigation && React.createElement(InPageNavigation, { ...inPageNavigation }))));
};
export default ContentHeroBanner;
